/**
 * Lightweight presence heartbeat. Fires immediately on mount / route change
 * and every 30 s while the tab is visible. Uses a stable client-generated
 * visitor_id in localStorage so anon visitors still get counted.
 *
 * Silent by design — any network error is swallowed. Presence is a nice-to-
 * have and must never surface errors to the user.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

const KEY = "sl_visitor_id";
const PING_MS = 30_000;

function getVisitorId() {
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = `v_${crypto.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36)}`;
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    // Private mode / storage disabled — fall back to a per-session id.
    return `v_ephemeral_${Math.random().toString(36).slice(2)}`;
  }
}

async function ping(path) {
  try {
    await api.post("/presence/ping", { visitor_id: getVisitorId(), path });
  } catch {
    /* silent */
  }
}

export function usePresenceHeartbeat() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Only ping while the tab is visible — no need to keep a hidden tab
    // counted as "online".
    let timer = null;
    const tick = () => {
      if (document.visibilityState === "visible") ping(pathname);
    };
    tick();
    timer = setInterval(tick, PING_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [pathname]);
}
